import {
  ColumnFiltersState,
  RowSelectionState,
  SortingState,
  VisibilityState,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table'
import { useEffect, useMemo, useState } from 'react'
import { useProductsClosedApi } from 'src/api/db/apiProductsClosed'
import { Row } from 'src/shared/components/Semantic/all'
import { Button, Dialog, DialogContent } from 'src/shared/components/ui'
import { useColumns } from './columns'
import { ActiveFiltersSection } from './components/ActiveFiltersSection'
import { PaginationControls } from './components/PaginationControls'
import { ProductsCountSection } from './components/ProductsCountSection'
import { ProductsTable } from './components/ProductsTable'
import { UnifiedFilterBar } from './components/UnifiedFilterBar'
import { DEFAULT_SORTING } from './consts'
import { useDebouncedSearch, useInventoryUrlParams } from './hooks'
import { transformProductData } from './utils'
import type { CategoryChange } from './types'
import { PageContainer } from 'src/shared/components/PageContainer'
import { Box, Plus, Trash2 } from 'lucide-react'
import { paths } from 'src/routes/paths'
import { useLocalization } from 'src/shared/hooks/useLocalization'
import { useNavigate } from 'react-router-dom'
import {
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from 'src/shared/components/ui/dialog'

export const ProductsPage = () => {
  const [sorting, setSorting] = useState<SortingState>(DEFAULT_SORTING)
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([])
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({})
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({})
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false)
  const [pendingCategoryChanges, setPendingCategoryChanges] = useState<
    CategoryChange[]
  >([])
  const { t } = useLocalization()
  const navigate = useNavigate()

  const {
    searchFilters,
    currentPage,
    pageSize,
    showKlayderRecommendedProducts,
    showWithoutCategories,
    showWithoutTranslations,
    showDraft,
    setSearchFilters,
    setCurrentPage,
    setPageSize,
    setShowKlayderRecommendedProducts,
    setShowWithoutCategories,
    setShowWithoutTranslations,
    setShowDraft,
    removeSearchFilter,
    clearAllFilters,
  } = useInventoryUrlParams()

  const debouncedSearchFilters = useDebouncedSearch(searchFilters)

  const { productsClosed, isProductsFetching, deleteProduct } =
    useProductsClosedApi({
      page: currentPage,
      pageSize,
      searchArticleNumber: debouncedSearchFilters.articleNumber,
      searchItemName: debouncedSearchFilters.itemName,
      searchSource: debouncedSearchFilters.source,
      searchCategory: debouncedSearchFilters.category,
      klayder_recommends: showKlayderRecommendedProducts,
      withoutCategories: showWithoutCategories,
      withoutTranslations: showWithoutTranslations,
      isDraft: showDraft,
    })

  useEffect(() => {
    setPendingCategoryChanges([])
  }, [productsClosed?.data, currentPage])

  const {
    columns,
    hasPendingCategoryChanges,
    applyCategoryChanges,
    isApplyingCategoryChanges,
  } = useColumns({
    pendingCategoryChanges,
    setPendingCategoryChanges,
  })

  const tableData = useMemo(
    () => transformProductData(productsClosed?.data as any),
    [productsClosed?.data]
  )

  const table = useReactTable({
    data: tableData,
    columns,
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onColumnVisibilityChange: setColumnVisibility,
    onRowSelectionChange: setRowSelection,
    getRowId: (row) => String(row.id),
    state: {
      sorting,
      columnFilters,
      columnVisibility,
      rowSelection,
      pagination: {
        pageIndex: currentPage - 1,
        pageSize,
      },
    },
    manualPagination: true,
    pageCount: productsClosed?.totalPages || 0,
    onPaginationChange: (updater) => {
      if (typeof updater === 'function') {
        const newState = updater({
          pageIndex: currentPage - 1,
          pageSize,
        })
        setCurrentPage(newState.pageIndex + 1)
        setPageSize(newState.pageSize)
      } else {
        setCurrentPage(updater.pageIndex + 1)
        setPageSize(updater.pageSize)
      }
    },
  })

  const hasActiveSearch = !!(
    searchFilters.articleNumber ||
    searchFilters.itemName ||
    searchFilters.source ||
    searchFilters.category
  )

  const selectedProductIds = Object.keys(rowSelection).map(Number)
  const selectedCount = selectedProductIds.length

  const handleBulkDelete = async () => {
    await Promise.all(
      selectedProductIds.map((id) => deleteProduct.mutateAsync(id))
    )
    setRowSelection({})
    setIsDeleteDialogOpen(false)
  }

  return (
    <>
      <PageContainer width="xl">
        <Row className="gap-2 items-center mb-1 py-2">
          <Box />
          <h1 className="text-2xl">{t('Inventory', 'Inventarie')}</h1>

          {selectedCount > 0 && (
            <Button
              variant="destructive"
              onClick={() => setIsDeleteDialogOpen(true)}
              className="ml-auto"
            >
              <Trash2 className="h-4 w-4 mr-2" />
              {t('Delete', 'Ta bort')} ({selectedCount})
            </Button>
          )}

          <Button
            variant="primary"
            onClick={() => navigate(`${paths.adminProducts}/new`)}
            className={selectedCount > 0 ? '' : 'ml-auto'}
          >
            <Plus className="h-4 w-4 mr-2" />
            {t('Add product', 'Lägg till produkt')}
          </Button>
        </Row>

        <UnifiedFilterBar
          searchFilters={searchFilters}
          onSearchChange={setSearchFilters}
          showKlayderRecommendedProducts={showKlayderRecommendedProducts}
          onKlayderRecommendedChange={setShowKlayderRecommendedProducts}
          showWithoutCategories={showWithoutCategories}
          onWithoutCategoriesChange={setShowWithoutCategories}
          showWithoutTranslations={showWithoutTranslations}
          onWithoutTranslationsChange={setShowWithoutTranslations}
          showDraft={showDraft}
          onDraftChange={setShowDraft}
          onClearAll={clearAllFilters}
        />

        <ActiveFiltersSection
          searchFilters={searchFilters}
          onRemoveSearchFilter={removeSearchFilter}
        />

        <div className="flex items-center justify-between pt-2">
          <ProductsCountSection
            count={productsClosed?.count ?? 0}
            hasActiveSearch={hasActiveSearch}
          />
        </div>

        {hasPendingCategoryChanges && (
          <Row className="gap-2 items-center py-2">
            <Button
              variant="primary"
              onClick={() => applyCategoryChanges()}
              disabled={isApplyingCategoryChanges}
            >
              {isApplyingCategoryChanges
                ? t('Saving...', 'Sparar...')
                : t('Save category changes', 'Spara kategoribyte')}
            </Button>
          </Row>
        )}
        <ProductsTable
          table={table}
          columns={columns}
          productsCount={productsClosed?.count ?? 0}
          hasActiveSearch={hasActiveSearch}
          isLoading={isProductsFetching}
        />

        <PaginationControls
          currentPage={currentPage}
          pageSize={pageSize}
          totalCount={productsClosed?.count ?? 0}
          totalPages={productsClosed?.totalPages || 0}
          onPageChange={setCurrentPage}
          onPageSizeChange={setPageSize}
        />
      </PageContainer>

      <Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {t('Confirm Deletion', 'Bekräfta borttagning')}
            </DialogTitle>
            <DialogDescription>
              {t(
                `Are you sure you want to delete ${selectedCount} product${
                  selectedCount > 1 ? 's' : ''
                }? This action cannot be undone.`,
                `Är du säker på att du vill ta bort ${selectedCount} produkt${
                  selectedCount > 1 ? 'er' : ''
                }? Denna åtgärd kan inte ångras.`
              )}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button onClick={() => setIsDeleteDialogOpen(false)}>
              {t('Cancel', 'Avbryt')}
            </Button>
            <Button
              variant="destructive"
              onClick={handleBulkDelete}
              disabled={deleteProduct.isPending}
            >
              {deleteProduct.isPending
                ? t('Deleting...', 'Tar bort...')
                : t('Delete', 'Ta bort')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}
